import React, { useMemo } from 'react';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { AccessTime } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  label?: string;
  size?: 'small' | 'medium';
  use24Hour?: boolean;
}

/**
 * Parse an "HH:MM" (or "HH:MM:SS") string into a Date anchored to today.
 * The date part is irrelevant — only hours/minutes are read back out on change.
 * Returns null for unparseable input so the picker shows its placeholder.
 */
export const parseTimeString = (time: string): Date | null => {
  if (!time || typeof time !== 'string') return null;
  const match = time.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return d;
};

export const formatTimeString = (date: Date | null): string | null => {
  if (!date || Number.isNaN(date.getTime())) return null;
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Time input for store hours. Renders a MUI DesktopTimePicker so the
 * primary interaction is typing (e.g. `03:07`) — the field is always a
 * text input, and the optional popover is a lightweight clock. This
 * avoids the responsive TimePicker's mobile-style clock modal (which
 * visually labels only 5-minute ticks) and preserves minute-level
 * precision.
 */
export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  label,
  size = 'small',
  use24Hour = true,
}) => {
  const dateValue = useMemo(() => parseTimeString(value), [value]);

  const handleChange = (next: Date | null) => {
    const formatted = formatTimeString(next);
    if (formatted !== null) {
      onChange(formatted);
    }
  };

  return (
    <DesktopTimePicker
      value={dateValue}
      onChange={handleChange}
      disabled={disabled}
      ampm={!use24Hour}
      label={label}
      views={['hours', 'minutes']}
      format={use24Hour ? 'HH:mm' : 'hh:mm a'}
      minutesStep={1}
      slotProps={{
        textField: {
          size,
          error,
          InputProps: {
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <AccessTime sx={{ fontSize: 18, color: 'action.active' }} />
              </InputAdornment>
            ),
          },
          sx: {
            minWidth: use24Hour ? 130 : 150,
            bgcolor: 'background.paper',
            '& .MuiOutlinedInput-input': {
              py: 1,
              fontSize: '14px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          },
        },
      }}
    />
  );
};
